import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import confetti from "canvas-confetti";
import html2canvas from "html2canvas";
import QRCode from "qrcode";

function CouponRedemption({ onClose }) {
  const [couponData, setCouponData] = useState(null);
  const [couponStyle, setCouponStyle] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const couponRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const storedCoupon = localStorage.getItem("coupon_data");
        const companyUsername = localStorage.getItem("company_username");

        if (!storedCoupon || !companyUsername) {
          throw new Error("Missing coupon data or company username.");
        }

        const parsedCoupon = JSON.parse(storedCoupon);
        if (!parsedCoupon?.unique_code) {
          throw new Error("Invalid coupon data.");
        }

        const [styleRes, qrUrl] = await Promise.all([
          fetch(
            `https://zola.technology/api/public/company/${companyUsername}/coupon-page/get/`
          ).then((res) => res.json()),
          QRCode.toDataURL(parsedCoupon.unique_code),
        ]);

        setCouponData(parsedCoupon);
        setCouponStyle(styleRes);
        setQrCodeUrl(qrUrl);

        confetti({
          particleCount: 900,
          spread: 400,
          origin: { y: 0.3 },
        });
      } catch (err) {
        console.error("Error loading coupon or style:", err);
        setError("Failed to load coupon.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (couponData) {
      const timeout = setTimeout(() => {
        localStorage.removeItem("coupon_data");
        localStorage.removeItem("zola_redirect_timer_start");
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [couponData]);

  const handleCopy = () => {
    if (couponData?.unique_code) {
      navigator.clipboard.writeText(couponData.unique_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async () => {
    if (!couponRef.current) return;

    try {
      await document.fonts.ready;
      const canvas = await html2canvas(couponRef.current, {
        useCORS: true,
        backgroundColor: "#fff",
        scale: 2,
      });

      const dataURL = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataURL;
      link.download = "your_coupon.png";
      link.click();
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  let discountDisplay = "Unknown OFF";
  if (couponData?.unique_code?.includes("%")) {
    discountDisplay = `${couponData.unique_code.split("-")[1]} OFF`;
  } else if (couponData?.unique_code?.includes("USD")) {
    discountDisplay = `$${couponData.unique_code.split("USD")[1].trim()} OFF`;
  }

  if (loading || !couponData || !couponStyle) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <div className="bg-white p-6 rounded-xl shadow-xl text-center">
          <p className="text-sm text-gray-600">Loading your coupon...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50">
      <div
        ref={couponRef}
        className="relative p-8 rounded-3xl shadow-2xl text-center max-w-md w-full"
        style={{
          backgroundColor: couponStyle?.background_color || "#ffffff",
          color: couponStyle?.text_color || "#000000",
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
        {couponStyle?.company_logo && (
          <div className="flex justify-center mb-4">
            <div
              className="w-48 h-48 rounded-full overflow-hidden border-4"
              style={{
                borderColor: couponStyle.discount_color || "#9333ea",
              }}
            >
              <img
                src={couponStyle.company_logo}
                alt="Company Logo"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        )}
        <h2
          className={`${
            couponStyle?.title_size || "text-3xl"
          } font-extrabold mb-2`}
          style={{ color: couponStyle?.title_color || "#000000" }}
        >
          {couponStyle?.thank_you_title || "THANK YOU"}
        </h2>

        <p
          className={`${couponStyle?.subtitle_size || "text-sm"} mb-6`}
          style={{ color: couponStyle?.subtitle_color || "#6b7280" }}
        >
          {couponStyle?.thank_you_subtitle || "for being a great customer"}
        </p>

        <p
          className={`${
            couponStyle?.discount_size || "text-5xl"
          } font-black mb-2`}
          style={{ color: couponStyle?.discount_color || "#9333ea" }}
        >
          {discountDisplay}
        </p>

        <p
          className={`${couponStyle?.note_size || "text-sm"} mb-6`}
          style={{ color: couponStyle?.note_color || "#6b7280" }}
        >
          {couponStyle?.note || "on your next order with us"}
        </p>

        {couponStyle?.show_qr_code && qrCodeUrl && (
          <div className="flex justify-center my-4">
            <img src={qrCodeUrl} alt="Coupon QR Code" className="w-28 h-28" />
          </div>
        )}

        <button
          onClick={handleCopy}
          className="w-full py-3 font-bold rounded-full mb-2 transition-all"
          style={{
            backgroundColor: couponStyle?.code_bg_color || "#ffffff",
            color: couponStyle?.code_text_color || "#7c3aed",
            border:
              couponStyle?.code_border_style === "none"
                ? "none"
                : `2px ${couponStyle?.code_border_style || "dashed"} ${
                    couponStyle?.code_border_color || "#7c3aed"
                  }`,
          }}
        >
          {couponData.unique_code}
        </button>

        <p className="text-xs text-purple-500 mt-1">
          {copied ? "✅ Code copied!" : "Tap to copy code"}
        </p>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
          {couponData.user_email && `Issued to: ${couponData.user_email}`}
        </p>

        {couponData.is_used ? (
          <p className="text-xs text-red-500 mt-2">⚠️ Coupon already used</p>
        ) : (
          <p className="text-xs text-green-500 mt-2">✅ Coupon is valid</p>
        )}

        <button
          onClick={handleDownload}
          className="w-full mt-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-full transition"
        >
          Download Coupon
        </button>

        {couponData?.coupon?.valid_until && (
          <p
            className={`${couponStyle?.validity_size || "text-xs"} mt-8`}
            style={{ color: couponStyle?.validity_color || "#9ca3af" }}
          >
            Offer valid until{" "}
            {new Date(couponData.coupon.valid_until).toLocaleDateString(
              undefined,
              {
                year: "numeric",
                month: "long",
                day: "numeric",
              }
            )}
          </p>
        )}
      </div>
    </div>
  );
}

export default CouponRedemption;
